import React, { useEffect, useRef, useState, useCallback } from 'react';

// Color themes configuration
export const THEME_PALETTES = {
  emerald: {
    name: 'زمردی سبز (Emerald Green)',
    bgGradient: 'from-emerald-950 via-emerald-800 to-emerald-950',
    bannerBorder: 'border-amber-400',
    goldAccent: '#fbbf24',
    textColor: 'text-amber-100',
    titleBadge: 'bg-amber-500/20 text-amber-300 border-amber-400/40',
    tasselColor: '#d97706',
    sparkleColors: ['#fbbf24', '#fef08a', '#10b981', '#34d399', '#ffffff'],
  },
  gold: {
    name: 'شاہی سنہری (Royal Gold)',
    bgGradient: 'from-amber-950 via-amber-700 to-amber-950',
    bannerBorder: 'border-yellow-300',
    goldAccent: '#fef08a',
    textColor: 'text-yellow-50',
    titleBadge: 'bg-yellow-400/20 text-yellow-200 border-yellow-300/40',
    tasselColor: '#b45309',
    sparkleColors: ['#fde047', '#fef08a', '#eab308', '#ffffff', '#f59e0b'],
  },
  crimson: {
    name: 'یاقوتی سرخ (Crimson Velvet)',
    bgGradient: 'from-rose-950 via-rose-800 to-rose-950',
    bannerBorder: 'border-amber-300',
    goldAccent: '#fde68a',
    textColor: 'text-rose-50',
    titleBadge: 'bg-amber-400/20 text-amber-200 border-amber-300/40',
    tasselColor: '#e11d48',
    sparkleColors: ['#f43f5e', '#fb7185', '#fbbf24', '#fef08a', '#ffffff'],
  },
  sapphire: {
    name: 'لاجوردی نیلا (Midnight Sapphire)',
    bgGradient: 'from-slate-950 via-blue-900 to-slate-950',
    bannerBorder: 'border-amber-300',
    goldAccent: '#93c5fd',
    textColor: 'text-blue-50',
    titleBadge: 'bg-blue-400/20 text-blue-200 border-blue-300/40',
    tasselColor: '#2563eb',
    sparkleColors: ['#60a5fa', '#93c5fd', '#38bdf8', '#fbbf24', '#ffffff'],
  },
  amnSky: {
    name: 'روایتی نیلا (AMN Classic Sky)',
    bgGradient: 'from-sky-950 via-sky-800 to-sky-950',
    bannerBorder: 'border-amber-300',
    goldAccent: '#bae6fd',
    textColor: 'text-sky-50',
    titleBadge: 'bg-sky-400/20 text-sky-200 border-sky-300/40',
    tasselColor: '#0284c7',
    sparkleColors: ['#38bdf8', '#7dd3fc', '#fbbf24', '#ffffff', '#e0f2fe'],
  }
};

export default function CelebrationBannerPreview({
  bannerText = 'جشنِ ولادت با سعادت مبارک',
  bannerTitle = 'جشنِ مسرت',
  themeColor = 'emerald',
  effectType = 'sparkles_rain',
  durationSeconds = 8,
  onClose,
  isPreviewMode = false,
}) {
  const canvasRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(durationSeconds);
  const [isClosing, setIsClosing] = useState(false);
  const animationFrameRef = useRef(null);

  const currentTheme = THEME_PALETTES[themeColor] || THEME_PALETTES.emerald;

  const handleTriggerClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 600);
  }, [onClose]);

  // Countdown timer
  useEffect(() => {
    setTimeLeft(durationSeconds);
    setIsClosing(false);

    if (durationSeconds <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTriggerClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [durationSeconds, handleTriggerClose]);

  // Sparkles / Confetti Canvas Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    // Generate particles
    const particleCount = effectType === 'confetti' ? 140 : 120;
    const particles = [];
    const colors = currentTheme.sparkleColors;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height - height,
        size: Math.random() * 5 + 2,
        speedY: Math.random() * 2.5 + 1.2,
        speedX: (Math.random() - 0.5) * 1.5,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.08,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.8 + 0.2,
        type: effectType === 'confetti' 
          ? (Math.random() > 0.4 ? 'rect' : 'circle') 
          : (Math.random() > 0.3 ? 'star' : 'sparkle'),
        twinkleSpeed: Math.random() * 0.05 + 0.02,
        twinklePhase: Math.random() * Math.PI,
        swayAmplitude: Math.random() * 30 + 10,
        swaySpeed: Math.random() * 0.02 + 0.01,
        initialX: Math.random() * width,
      });
    }

    let frame = 0;

    const drawStar = (cx, cy, spikes, outerRadius, innerRadius, fill) => {
      let rot = (Math.PI / 2) * 3;
      let x = cx;
      let y = cy;
      const step = Math.PI / spikes;

      ctx.beginPath();
      ctx.moveTo(cx, cy - outerRadius);
      for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerRadius;
        y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;

        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
      }
      ctx.lineTo(cx, cy - outerRadius);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
    };

    const drawShineSparkle = (cx, cy, size, fill) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.fillStyle = fill;
      
      // Draw 4-point diamond star
      ctx.beginPath();
      ctx.moveTo(0, -size * 1.8);
      ctx.quadraticCurveTo(0, 0, size * 1.8, 0);
      ctx.quadraticCurveTo(0, 0, 0, size * 1.8);
      ctx.quadraticCurveTo(0, 0, -size * 1.8, 0);
      ctx.quadraticCurveTo(0, 0, 0, -size * 1.8);
      ctx.closePath();
      ctx.fill();
      
      // Core bright center
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      
      ctx.restore();
    };

    const render = () => {
      frame++;
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;
        p.x = p.initialX + Math.sin(frame * p.swaySpeed + p.initialX) * p.swayAmplitude;

        // Twinkle opacity
        const currentOpacity = Math.max(0.1, Math.min(1, p.opacity + Math.sin(frame * p.twinkleSpeed + p.twinklePhase) * 0.3));

        if (p.y > height + 20) {
          p.y = -20;
          p.initialX = Math.random() * width;
          p.x = p.initialX;
        }

        ctx.save();
        ctx.globalAlpha = currentOpacity;

        if (p.type === 'star') {
          drawStar(p.x, p.y, 5, p.size * 1.6, p.size * 0.7, p.color);
        } else if (p.type === 'sparkle') {
          drawShineSparkle(p.x, p.y, p.size, p.color);
        } else if (p.type === 'rect') {
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        }

        ctx.restore();
      });

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [themeColor, effectType, currentTheme.sparkleColors]);

  return (
    <div
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-start pointer-events-none transition-opacity duration-500 select-none ${
        isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      }`}
      style={{ perspective: '1200px' }}
    >
      {/* Particle Canvas Overlay */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
      />

      {/* Top Floating Action Controls */}
      <div className="relative z-50 w-full max-w-4xl px-4 pt-4 flex justify-between items-center pointer-events-auto">
        <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full border border-white/20 shadow-lg">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
          <span>{isPreviewMode ? 'لائیو پریویو موڈ (Live Preview)' : 'مبارکباد'}</span>
          {durationSeconds > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-[11px] font-mono">
              {timeLeft}s
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleTriggerClose}
          className="bg-black/60 hover:bg-black/80 text-white hover:text-amber-300 border border-white/20 hover:border-amber-400/50 p-2 rounded-full shadow-lg transition-all transform hover:scale-110 active:scale-95"
          title="بند کریں (Close)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 3D Cloth Waving Banner Container */}
      <div className="relative z-40 mt-3 md:mt-6 w-full max-w-4xl px-4 pointer-events-auto flex justify-center">
        <div className="relative w-full group">
          
          {/* Banner Suspension Strings / Rod */}
          <div className="w-full flex justify-between items-center px-8 -mb-2">
            <div className="w-4 h-4 rounded-full bg-amber-400 border-2 border-amber-600 shadow-md transform -translate-y-1" />
            <div className="h-1 flex-1 mx-2 bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-400 rounded-full shadow" />
            <div className="w-4 h-4 rounded-full bg-amber-400 border-2 border-amber-600 shadow-md transform -translate-y-1" />
          </div>

          {/* Realistic Waving Fabric Banner */}
          <div
            className={`relative w-full rounded-2xl md:rounded-3xl p-5 md:p-8 bg-gradient-to-r ${currentTheme.bgGradient} border-2 md:border-4 ${currentTheme.bannerBorder} shadow-[0_20px_50px_rgba(0,0,0,0.6),0_0_30px_rgba(251,191,36,0.3)] overflow-hidden transition-transform`}
            style={{
              animation: 'clothWave 4s ease-in-out infinite alternate',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Animated Dynamic Fabric Waves Overlay (Sine Ripple Light Effect) */}
            <div
              className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, transparent, rgba(255,255,255,0.3) 40px, transparent 80px, rgba(0,0,0,0.4) 120px, transparent 160px)',
                backgroundSize: '320px 100%',
                animation: 'fabricShimmer 3s linear infinite',
              }}
            />

            {/* Embroidered Islamic Border Pattern Effect */}
            <div className="absolute inset-1.5 md:inset-2.5 rounded-xl md:rounded-2xl border border-dashed border-amber-400/40 pointer-events-none" />

            {/* Glowing Corner Accents */}
            <div className="absolute top-2 left-2 text-amber-300 text-lg md:text-xl select-none">✦</div>
            <div className="absolute top-2 right-2 text-amber-300 text-lg md:text-xl select-none">✦</div>
            <div className="absolute bottom-2 left-2 text-amber-300 text-lg md:text-xl select-none">✦</div>
            <div className="absolute bottom-2 right-2 text-amber-300 text-lg md:text-xl select-none">✦</div>

            {/* Banner Content */}
            <div className="relative z-10 text-center flex flex-col items-center justify-center space-y-2 md:space-y-3">
              {/* Optional Title Badge */}
              {bannerTitle && (
                <div className={`inline-flex items-center gap-1.5 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-semibold border ${currentTheme.titleBadge} shadow-inner`}>
                  <span className="text-amber-400 animate-pulse">★</span>
                  <span>{bannerTitle}</span>
                  <span className="text-amber-400 animate-pulse">★</span>
                </div>
              )}

              {/* Main Dynamic Sentence with Rich Nastaleeq Typography */}
              <h1
                dir="rtl"
                className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold ${currentTheme.textColor} leading-relaxed md:leading-relaxed drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]`}
                style={{
                  fontFamily: '"Jameel Noori Nastaleeq Kasheeda", "Jameel Noori Nastaleeq Regular", "Al Majeed Quranic Font", serif',
                  textShadow: '0 2px 10px rgba(0,0,0,0.7), 0 0 20px rgba(251,191,36,0.4)',
                }}
              >
                {bannerText}
              </h1>

              {/* Subtle Decorative Golden Divider */}
              <div className="flex items-center justify-center gap-2 pt-1 w-full max-w-xs opacity-75">
                <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent flex-1" />
                <span className="text-amber-300 text-xs">﷽</span>
                <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent flex-1" />
              </div>
            </div>

            {/* Bottom Golden Tassels Decoration */}
            <div className="absolute -bottom-1 left-0 right-0 flex justify-around pointer-events-none">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 md:w-2 h-3 md:h-4 bg-gradient-to-b from-amber-400 to-amber-600 rounded-b shadow-sm transform origin-top"
                  style={{
                    animation: `tasselSwing 2.5s ease-in-out infinite alternate ${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Wave Keyframes */}
      <style>{`
        @keyframes clothWave {
          0% {
            transform: rotateX(4deg) rotateY(-2deg) skewY(-0.5deg);
          }
          50% {
            transform: rotateX(-3deg) rotateY(2deg) skewY(0.5deg);
          }
          100% {
            transform: rotateX(4deg) rotateY(-2deg) skewY(-0.5deg);
          }
        }
        @keyframes fabricShimmer {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 320px 0;
          }
        }
        @keyframes tasselSwing {
          0% {
            transform: rotate(-15deg);
          }
          100% {
            transform: rotate(15deg);
          }
        }
      `}</style>
    </div>
  );
}
